/* eslint-disable max-len */
/* eslint-disable no-shadow */
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useCallback, useEffect, useState } from 'react'
import {
  FeatureGroup,
  GeoJSON,
  LayerGroup,
  LayersControl,
  MapContainer,
  Marker,
  Pane,
  Tooltip,
} from 'react-leaflet'
import { manualSite, manualSiteNew } from '../../assets/leafletIcons'
import { MANUAL_SITE_OPACITY, randomSiteIcon } from '../Map/monitoringSiteMarkers'
import { PANEL_HEIGHT } from '../../utils/constants'
import { getRandomColor } from '../../utils/mapDrawHandle'
import BaseLayers from '../map/BaseLayers'

const EMPTY_LAYERS = []

// GeoJSON reads `data` only on mount, so a refetch that lands after the tab opens would never be
// drawn. The feature sets are memoized upstream on their query data or seed, so a new identity
// means new geometry: keying on it remounts the layer exactly then.
const featureSetIds = new WeakMap()
let lastFeatureSetId = 0
const featureSetKey = featureSet => {
  if (!featureSetIds.has(featureSet)) {
    lastFeatureSetId += 1
    featureSetIds.set(featureSet, lastFeatureSetId)
  }
  return featureSetIds.get(featureSet)
}

const SamplePlanningMap = ({
  stratas,
  monitoringSitesBySamplingAreas,
  perimeter,
  layers = EMPTY_LAYERS,
  onMapClick,
}) => {
  const [map, setMap] = useState()

  const handleStyle = feature => {
    return {
      fillColor: feature.properties.color,
      color: feature.properties.color,
      weight: 3,
      opacity: 1,
      fillOpacity: 0.3,
    }
  }

  useEffect(() => {
    if (map && stratas) {
      map.fitBounds(L.geoJSON(stratas).getBounds(), { padding: [50, 50] })
    }
  }, [map, perimeter, stratas])

  const getColor = layer => {
    if (layer.name.includes('Loteo')) {
      return getRandomColor()
    }

    return layer.features[0]?.properties?.color
      ? layer.features[0]?.properties?.color
      : getRandomColor()
  }

  const handleMouseOver = event => {
    const { layer } = event
    const tooltipContent = layer?.feature?.properties?.name || layer?.feature?.properties?.label
    layer.bindTooltip(tooltipContent).openTooltip()
  }

  const handleLayerClick = useCallback(
    event => {
      const lat = Number(event.latlng.lat.toFixed(6))
      const lng = Number(event.latlng.lng.toFixed(6))
      onMapClick(lat, lng)
    },
    [onMapClick],
  )

  return (
    <Box sx={{ width: '100%', height: PANEL_HEIGHT }}>
      <MapContainer
        ref={setMap}
        scrollWheelZoom
        zoomControl
        attributionControl={false}
        closePopupOnClick={false}
        style={{ height: '100%', width: '100%' }}
        wheelPxPerZoomLevel={60}
        zoom={5}
        zoomDelta={1}
        zoomSnap={0.1}
      >
        {perimeter && (
          <GeoJSON
            data={perimeter}
            eventHandlers={{
              click: handleLayerClick,
            }}
            style={{
              color: 'black',
              weight: 3,
              opacity: 1,
              fillOpacity: 0,
            }}
          />
        )}
        <LayersControl collapsed={false}>
          <BaseLayers />
          {layers.map(layer => (
            <Pane key={layer.name} name={layer.name}>
              <LayersControl.Overlay name={layer.name}>
                <FeatureGroup>
                  <LayerGroup>
                    <GeoJSON
                      key={featureSetKey(layer.features)}
                      data={layer.features}
                      eventHandlers={{
                        mouseover: handleMouseOver,
                      }}
                      style={() => {
                        return {
                          color: getColor(layer),
                          weight: 2,
                          opacity: 1,
                          fillOpacity: 0.4,
                        }
                      }}
                    />
                  </LayerGroup>
                </FeatureGroup>
              </LayersControl.Overlay>
            </Pane>
          ))}
        </LayersControl>
        <FeatureGroup>
          <LayerGroup>
            {stratas && (
              <GeoJSON
                key={featureSetKey(stratas)}
                data={stratas}
                style={handleStyle}
                onEachFeature={(feature, layer) => {
                  const baseLabel = `${feature.properties.name} - Click para agregar sitio manual`
                  layer.bindTooltip(baseLabel, {
                    permanent: false,
                    direction: 'left',
                    sticky: true,
                    className: 'strata-label',
                  })
                  layer.addEventListener('click', handleLayerClick)
                  layer.addEventListener('mousemove', event => {
                    const { lat, lng } = event.latlng || {}
                    if (typeof lat === 'number' && typeof lng === 'number') {
                      const coords = `Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`
                      layer.setTooltipContent(`${baseLabel}<br/>${coords}`)
                    }
                  })
                }}
              />
            )}
          </LayerGroup>
        </FeatureGroup>
        {monitoringSitesBySamplingAreas &&
          stratas &&
          stratas.features.map(strata => {
            return (
              <FeatureGroup key={strata.properties.name}>
                <LayerGroup>
                  {monitoringSitesBySamplingAreas[strata.properties.name] &&
                    monitoringSitesBySamplingAreas[strata.properties.name].map(marker => {
                      if (marker.properties.isRandomSite) {
                        return (
                          <Marker
                            key={marker.properties.name}
                            icon={randomSiteIcon(strata.properties.color)}
                            position={[
                              marker.geometry.coordinates[1],
                              marker.geometry.coordinates[0],
                            ]}
                          >
                            <Tooltip>
                              <div>
                                <Typography variant="p">{marker.properties.name}</Typography>
                              </div>
                            </Tooltip>
                          </Marker>
                        )
                      }
                      if (marker.properties.newManualSite) {
                        return (
                          <Marker
                            key={marker.properties.name}
                            className="bouncing-marker"
                            icon={manualSiteNew.leafletIcon}
                            position={[
                              marker.geometry.coordinates[1],
                              marker.geometry.coordinates[0],
                            ]}
                          >
                            <Tooltip>
                              <div>
                                <Typography variant="p">{marker.properties.name}</Typography>
                              </div>
                            </Tooltip>
                          </Marker>
                        )
                      }
                      return (
                        <Marker
                          key={marker.properties.name}
                          icon={manualSite.leafletIcon}
                          opacity={MANUAL_SITE_OPACITY}
                          position={[
                            marker.geometry.coordinates[1],
                            marker.geometry.coordinates[0],
                          ]}
                        >
                          <Tooltip>
                            <div>
                              <Typography variant="p">{marker.properties.name}</Typography>
                            </div>
                          </Tooltip>
                        </Marker>
                      )
                    })}
                </LayerGroup>
              </FeatureGroup>
            )
          })}
      </MapContainer>
    </Box>
  )
}

export default SamplePlanningMap
